import React, { useCallback, useState } from 'react';
import { offHours, offHoursConfirmCopy, offHoursTooltip } from './off_hours';
import type { ContactAction, OffHoursInfo } from './off_hours';
import type { CallWindow } from './local_time';
import { Btn, SalesDialog } from './ui';

interface Pending {
  action: ContactAction;
  info: OffHoursInfo;
  run: () => void;
}

interface DialogProps {
  pending: Pending | null;
  on_cancel: () => void;
  on_confirm: () => void;
}

const DETAIL_ID = 'off-hours-detail';

// Same stacking as the CustomModal this replaced: sends from the SMS window and
// chat popup (z 10000+) confirm here, so the dialog must sit above them.
const ABOVE_POPUPS = 999999999;

// Cancel is the first button in the dialog, so SalesDialog focuses it: Enter stays safe.
export function OffHoursConfirmDialog({ pending, on_cancel, on_confirm }: DialogProps) {
  const copy = pending ? offHoursConfirmCopy(pending.action) : null;
  return (
    <SalesDialog
      open={!!pending && !!copy}
      onClose={on_cancel}
      role="alertdialog"
      title={copy?.question || ''}
      describedBy={DETAIL_ID}
      zIndex={ABOVE_POPUPS}
      footer={
        <>
          <Btn variant="neutral" onClick={on_cancel}>
            Cancel
          </Btn>
          <Btn variant="primary" onClick={on_confirm}>
            {copy?.confirm_label}
          </Btn>
        </>
      }
    >
      {pending ? (
        <div id={DETAIL_ID} className="px-4 py-3.5 text-[13px] text-content-secondary">
          {offHoursTooltip(pending.info)}
        </div>
      ) : null}
    </SalesDialog>
  );
}

// guard(action, run): runs now inside calling hours (or with no usable zone),
// else asks first. Render `dialog` once in the page.
export function useOffHoursGate(
  zone: string | null | undefined,
  call_window: CallWindow | null | undefined
): { guard: (action: ContactAction, run: () => void) => void; dialog: React.ReactElement } {
  const [pending, set_pending] = useState<Pending | null>(null);
  const guard = useCallback(
    (action: ContactAction, run: () => void) => {
      const info = offHours(zone, Date.now(), call_window);
      if (!info) {
        run();
        return;
      }
      set_pending({ action, info, run });
    },
    [zone, call_window]
  );
  const on_cancel = useCallback(() => set_pending(null), []);
  const on_confirm = useCallback(() => {
    set_pending(null);
    pending?.run();
  }, [pending]);
  return {
    guard,
    dialog: (
      <OffHoursConfirmDialog pending={pending} on_cancel={on_cancel} on_confirm={on_confirm} />
    )
  };
}
